import { useState, useEffect } from 'react';
import { getAuthHeaders, getAuthHeadersJson } from '../utils/auth';
import {
  Button,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Avatar,
  AvatarFallback,
  Badge,
  Switch,
  Separator,
} from '@rnd-payplus/alloy-react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { usePageShell } from '../contexts/PageShellContext';

const ProfilePage = () => {
  const [loading, setLoading] = useState(true);

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    timezone: 'UTC',
    language: 'en-US',
    emailNotifications: true,
    slackNotifications: false,
  });

  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const [meRes, prefRes] = await Promise.all([
          fetch('/api/users/me', { headers: getAuthHeaders() }),
          fetch('/api/users/me/preferences', { headers: getAuthHeaders() }),
        ]);

        if (meRes.ok) {
          const me = await meRes.json();
          const [firstName = '', ...rest] = (me.name || '').split(' ');
          const lastName = rest.join(' ');
          setProfile(p => ({ ...p, email: me.email, firstName, lastName }));
        }

        if (prefRes.ok) {
          const prefs = await prefRes.json();
          setProfile(p => ({
            ...p,
            timezone: prefs.timezone ?? 'UTC',
            language: prefs.language ?? 'en-US',
            emailNotifications: prefs.emailNotifications ?? true,
            slackNotifications: prefs.slackNotifications ?? false,
          }));
        }
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleSaveProfile = async () => {
    const name = `${profile.firstName} ${profile.lastName}`.trim();

    // Save profile and preferences independently — a failure in one
    // must not prevent the other from persisting. Report partial success
    // so the user knows exactly what landed.
    const [meResult, prefResult] = await Promise.allSettled([
      fetch('/api/users/me', {
        method: 'PUT',
        headers: getAuthHeadersJson(),
        body: JSON.stringify({ name, email: profile.email }),
      }).then(async res => {
        if (!res.ok) throw new Error(`Profile save failed (${res.status})`);
        return res;
      }),
      fetch('/api/users/me/preferences', {
        method: 'PUT',
        headers: getAuthHeadersJson(),
        body: JSON.stringify({
          emailNotifications: profile.emailNotifications,
          slackNotifications: profile.slackNotifications,
          timezone: profile.timezone,
          language: profile.language,
        }),
      }).then(async res => {
        if (!res.ok) throw new Error(`Preferences save failed (${res.status})`);
        return res;
      }),
    ]);

    const meOk = meResult.status === 'fulfilled';
    const prefOk = prefResult.status === 'fulfilled';

    if (meOk && prefOk) {
      toast.success('Profile updated successfully!');
    } else if (meOk && !prefOk) {
      const reason = prefResult.reason instanceof Error ? prefResult.reason.message : 'unknown error';
      toast.error(`Profile saved, but preferences failed: ${reason}`);
    } else if (!meOk && prefOk) {
      const reason = meResult.reason instanceof Error ? meResult.reason.message : 'unknown error';
      toast.error(`Preferences saved, but profile failed: ${reason}`);
    } else {
      toast.error('Failed to save profile and preferences. Please try again.');
    }
  };

  const handleChangePassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }
    const res = await fetch('/api/users/me/password', {
      method: 'PUT',
      headers: getAuthHeadersJson(),
      body: JSON.stringify({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      }),
    });
    if (res.ok) {
      toast.success('Password changed successfully!');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } else {
      try {
        const data = await res.json();
        toast.error(data.error || 'Failed to change password');
      } catch {
        toast.error('Failed to change password');
      }
    }
  };

  usePageShell({
    title: 'User Profile',
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Profile' },
    ],
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <Tabs defaultValue="profile">
        <TabsList className="w-full">
          <TabsTrigger className="flex-1" value="profile">Profile</TabsTrigger>
          <TabsTrigger className="flex-1" value="security">Security</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
            <h2 className="text-base font-medium leading-6 mb-4">Profile Information</h2>
            <div className="flex items-center gap-4 mb-6">
                <Avatar className="h-20 w-20 text-2xl">
                  <AvatarFallback>
                    {profile.firstName[0]}{profile.lastName[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">
                    {profile.firstName} {profile.lastName}
                  </h3>
                  <p className="text-sm text-muted-foreground">{profile.email}</p>
                </div>
              </div>

              <Separator className="my-6" />

              <h3 className="text-lg font-semibold mb-4">Personal Information</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={profile.firstName}
                    onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={profile.lastName}
                    onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <select
                    id="timezone"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={profile.timezone}
                    onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="Europe/London">London</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <select
                    id="language"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={profile.language}
                    onChange={(e) => setProfile({ ...profile, language: e.target.value })}
                  >
                    <option value="en-US">English (US)</option>
                    <option value="en-GB">English (UK)</option>
                    <option value="es-ES">Spanish</option>
                    <option value="fr-FR">French</option>
                    <option value="de-DE">German</option>
                  </select>
                </div>
              </div>

            <Button className="mt-6" onClick={handleSaveProfile}>
              <Save className="mr-2 h-4 w-4" />
              Save Profile
            </Button>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
            <h2 className="text-base font-medium leading-6 mb-4">Change Password</h2>
            <div className="max-w-md space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwords.currentPassword}
                    onChange={(e) =>
                      setPasswords({ ...passwords, currentPassword: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwords.newPassword}
                    onChange={(e) =>
                      setPasswords({ ...passwords, newPassword: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwords.confirmPassword}
                    onChange={(e) =>
                      setPasswords({ ...passwords, confirmPassword: e.target.value })
                    }
                  />
                </div>
                <Button className="w-full" onClick={handleChangePassword}>
                  Change Password
                </Button>
            </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;
